import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { useCustomerPaymentsByCustomer, useCreateCustomerPayment, useDeleteCustomerPayment } from '../../../hooks/useCustomerPayments';
import { useAccounts, flattenAccounts } from '../../../hooks/useAccounts';
import { getCurrentCompanyId, getCurrentUserId } from '../../../utils/auth';

const CustomerPaymentsModal = ({ isOpen, onClose, customer }) => {
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, paymentId: null });
  const [paymentForm, setPaymentForm] = useState({
    accountId: '',
    amount: '',
    transactionDate: new Date().toISOString().split('T')[0],
    paymentReference: '',
    statusId: 1
  });

  const companyId = getCurrentCompanyId();

  const getStatusName = (statusId) => {
    switch (statusId) {
      case 1: return 'Pending';
      case 2: return 'Completed';
      case 3: return 'Failed';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (statusId) => {
    switch (statusId) {
      case 1: return 'bg-yellow-100 text-yellow-800';
      case 2: return 'bg-green-100 text-green-800';
      case 3: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const { data: payments = [], isLoading: paymentsLoading, refetch } = useCustomerPaymentsByCustomer(
    companyId,
    customer?.id
  );

  const { data: accountsData = [], isLoading: accountsLoading } = useAccounts(companyId);
  const accounts = flattenAccounts(accountsData).filter(account =>
    account.type === 'Asset' && account.isActive
  );

  const createPaymentMutation = useCreateCustomerPayment();
  const deletePaymentMutation = useDeleteCustomerPayment();

  useEffect(() => {
    if (isOpen) {
      setIsAddingPayment(false);
      setPaymentForm({
        accountId: '',
        amount: '',
        transactionDate: new Date().toISOString().split('T')[0],
        paymentReference: '',
        statusId: 1
      });
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setPaymentForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();

    if (!paymentForm.accountId || !paymentForm.amount || !paymentForm.transactionDate) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await createPaymentMutation.mutateAsync({
        customerId: customer.id,
        companyId,
        accountId: paymentForm.accountId,
        amount: parseFloat(paymentForm.amount),
        transactionDate: paymentForm.transactionDate,
        paymentReference: paymentForm.paymentReference,
        statusId: paymentForm.statusId,
        addedBy: getCurrentUserId()
      });

      setPaymentForm({
        accountId: '',
        amount: '',
        transactionDate: new Date().toISOString().split('T')[0],
        paymentReference: '',
        statusId: 1
      });
      setIsAddingPayment(false);
      refetch();
    } catch (error) {
      console.error('Error adding payment:', error);
      alert('Failed to add payment. Please try again.');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deletePaymentMutation.mutateAsync(deleteConfirm.paymentId);
      refetch();
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('Failed to delete payment. Please try again.');
    } finally {
      setDeleteConfirm({ isOpen: false, paymentId: null });
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(amount);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-KE');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Customer Payments</h2>
            <p className="text-sm text-gray-600 mt-1">{customer?.customerName || customer?.companyName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Add Payment Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Payments</h3>
              <Button
                onClick={() => setIsAddingPayment(!isAddingPayment)}
                variant={isAddingPayment ? 'outline' : 'default'}
                size="sm"
              >
                {isAddingPayment ? 'Cancel' : 'Add Payment'}
              </Button>
            </div>

            {isAddingPayment && (
              <form onSubmit={handleAddPayment} className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Select
                    label="Account"
                    value={paymentForm.accountId}
                    onChange={(value) => handleInputChange('accountId', value)}
                    options={accounts.map(account => ({
                      value: account.id,
                      label: account.name,
                      key: account.id
                    }))}
                    placeholder={accountsLoading ? 'Loading accounts...' : `Select account (${accounts.length} available)`}
                    loading={accountsLoading}
                    required
                  />

                  <Input
                    label="Amount"
                    type="number"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    placeholder="0.00"
                    required
                  />

                  <Input
                    label="Transaction Date"
                    type="date"
                    value={paymentForm.transactionDate}
                    onChange={(e) => handleInputChange('transactionDate', e.target.value)}
                    required
                  />

                  <Input
                    label="Payment Reference"
                    type="text"
                    value={paymentForm.paymentReference}
                    onChange={(e) => handleInputChange('paymentReference', e.target.value)}
                    placeholder="e.g. MPESA code, cheque no."
                  />

                  <div className="flex items-end">
                    <Button
                      type="submit"
                      variant="default"
                      size="md"
                      disabled={createPaymentMutation.isPending}
                      className="w-full"
                    >
                      {createPaymentMutation.isPending ? 'Adding...' : 'Add Payment'}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Payments List */}
          <div>
            {paymentsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
                  <p className="mt-2 text-sm text-gray-600">Loading payments...</p>
                </div>
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No payments found for this customer</p>
                <p className="text-sm text-gray-400 mt-1">Add a payment to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {payments.map((payment) => (
                  <div
                    key={payment.Id}
                    className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-6 flex-wrap">
                        <div>
                          <p className="font-medium text-gray-900">{formatCurrency(payment.Amount)}</p>
                          <p className="text-sm text-gray-600">{payment.AccountName}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">{formatDate(payment.TransactionDate)}</p>
                          <p className="text-xs text-gray-500">Added: {formatDate(payment.DateAdded)}</p>
                        </div>
                        {payment.PaymentReference && (
                          <div>
                            <p className="text-xs text-gray-500">Reference</p>
                            <p className="text-sm font-medium text-gray-700">{payment.PaymentReference}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(payment.StatusId)}`}>
                        {getStatusName(payment.StatusId)}
                      </span>

                      <Button
                        onClick={() => setDeleteConfirm({ isOpen: true, paymentId: payment.Id })}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                        disabled={deletePaymentMutation.isPending}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <Button onClick={onClose} variant="outline">Close</Button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Payment"
        message="Are you sure you want to delete this payment? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, paymentId: null })}
      />
    </div>
  );
};

export default CustomerPaymentsModal;
